(() => {
  const headingSelector = 'h1, h2, h3, h4, h5, h6';
  const genericHeading = /^(caract[ée]ristiques?( essentielles?| principales?| du produit)?|d[ée]tails?( du produit)?|description|product details?|key features?)\s*:?$/i;
  const sizeHeading = /(guide (des )?(tailles?|pointures?)|tableau (des )?(tailles?|pointures?)|dimensions?|mensurations?|measurements?|size (guide|chart)|product measurements?)/i;
  const sizeLabel = /^(tailles?( disponibles?)?|pointures?( disponibles?)?|dimensions?|mensurations?|measurements?|size(s| guide| chart)?)\s*:?/i;
  const sizeAdvice = /(mesures? (peuvent|pourraient) varier|choisissez la taille|taille sup[ée]rieure|r[ée]f[ée]rez-vous aux mesures|conseil dija|size may vary|choose the larger size|refer to the measurements)/i;
  const packageHeading = /^(contenu du colis|contenu de l['’]emballage|packing list|package content)/i;

  const normalize = (value) => (value || '').replace(/\s+/g, ' ').trim();

  function unwrapDescription(source) {
    const onlyChild = source.children.length === 1 ? source.firstElementChild : null;
    if (!onlyChild?.classList.contains('dija-product-description')) return;

    const fragment = document.createDocumentFragment();
    while (onlyChild.firstChild) fragment.appendChild(onlyChild.firstChild);
    onlyChild.replaceWith(fragment);
  }

  function wrapTables(container) {
    container.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList.contains('dija-product-table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'dija-product-table-scroll';
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region');
      wrapper.setAttribute('aria-label', 'Tableau de dimensions');
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  function buildAccordion(title, fragment, id) {
    const wrapper = document.createElement('div');
    wrapper.className = 'product__accordion accordion dija-product-accordion';

    const details = document.createElement('details');
    details.id = id;

    const summary = document.createElement('summary');
    const label = document.createElement('span');
    label.className = 'dija-product-accordion__title';
    label.textContent = title;
    const icon = document.createElement('span');
    icon.className = 'dija-product-accordion__icon';
    icon.setAttribute('aria-hidden', 'true');
    summary.append(label, icon);

    const content = document.createElement('div');
    content.className = 'accordion__content rte dija-product-description';
    content.appendChild(fragment);
    wrapTables(content);

    details.append(summary, content);
    wrapper.appendChild(details);
    return wrapper;
  }

  function isStrongLabel(node, pattern) {
    if (node?.tagName !== 'P') return false;
    const strong = node.querySelector(':scope > strong:first-child');
    return Boolean(strong && pattern.test(normalize(strong.textContent)));
  }

  function isGenericSectionHeading(node) {
    if (!node) return false;
    if (node.matches?.(headingSelector)) return genericHeading.test(normalize(node.textContent));
    return isStrongLabel(node, genericHeading) && normalize(node.textContent).length < 90;
  }

  function isSizeSectionHeading(node) {
    if (!node) return false;
    const text = normalize(node.textContent);
    if (node.matches?.(headingSelector)) return sizeHeading.test(text);
    return isStrongLabel(node, sizeHeading) && text.length < 110;
  }

  function hasProductImages(node) {
    return Boolean(node?.querySelector?.('img, video, iframe'));
  }

  function cleanLead(lead) {
    if (!lead?.classList.contains('dija-product-lead')) return;
    const repeatedTitle = lead.querySelector(':scope > strong:first-child');
    if (!repeatedTitle) return;

    const followingNode = repeatedTitle.nextSibling;
    repeatedTitle.remove();
    if (followingNode?.nodeType === Node.TEXT_NODE) {
      followingNode.textContent = followingNode.textContent.replace(/^\s*[—–-]\s*/, '');
    }
  }

  function extractIntro(source, intro) {
    let children = Array.from(source.children);
    while (children[0] && isGenericSectionHeading(children[0])) {
      children[0].remove();
      children = Array.from(source.children);
    }

    const firstParagraph = children.find((node) => node.tagName === 'P' && normalize(node.textContent));
    if (!firstParagraph) return;

    const content = intro.querySelector('[data-dija-product-intro-content]');
    const previous = firstParagraph.previousElementSibling;
    if (previous?.matches(headingSelector) && !genericHeading.test(normalize(previous.textContent)) && !sizeHeading.test(normalize(previous.textContent))) {
      content.appendChild(previous);
    }

    const isShortHeadline = normalize(firstParagraph.textContent).length < 115
      && firstParagraph.children.length === 1
      && firstParagraph.firstElementChild?.tagName === 'STRONG';
    const nextParagraph = firstParagraph.nextElementSibling;

    if (isShortHeadline) firstParagraph.classList.add('dija-product-intro__headline');
    cleanLead(firstParagraph);
    content.appendChild(firstParagraph);

    if (isShortHeadline && nextParagraph?.tagName === 'P' && normalize(nextParagraph.textContent).length > 45) {
      content.appendChild(nextParagraph);
    }

    intro.hidden = false;
  }

  function inlineSizeItem(item) {
    const strong = item.querySelector(':scope > strong:first-child');
    if (!strong) return false;
    const label = normalize(strong.textContent);
    if (sizeLabel.test(label)) return true;

    if (/^taille\s*:?$/i.test(label)) {
      const value = normalize(item.textContent).replace(label, '');
      return /(^|[\s,\/])(xxs|xs|s|m|l|xl|xxl|\d{2,3})([\s,\/]|$)/i.test(value);
    }
    return false;
  }

  function extractSizeSections(source) {
    const fragment = document.createDocumentFragment();
    let capturing = false;

    Array.from(source.children).forEach((node) => {
      const text = normalize(node.textContent);

      if (isSizeSectionHeading(node)) {
        capturing = true;
        node.remove();
        return;
      }

      if (node.tagName === 'UL' || node.tagName === 'OL') {
        const matchingItems = Array.from(node.children).filter(inlineSizeItem);
        if (matchingItems.length) {
          const list = document.createElement(node.tagName.toLowerCase());
          matchingItems.forEach((item) => list.appendChild(item));
          fragment.appendChild(list);
          if (!node.children.length) node.remove();
        }
        if (!capturing) return;
      }

      if (node.tagName === 'TABLE') {
        fragment.appendChild(node);
        capturing = true;
        return;
      }

      if (node.tagName === 'P' && isStrongLabel(node, sizeLabel)) {
        fragment.appendChild(node);
        return;
      }

      if (capturing) {
        if (packageHeading.test(text) || hasProductImages(node)) {
          capturing = false;
          return;
        }
        if (node.matches?.(headingSelector) || isGenericSectionHeading(node)) {
          capturing = false;
          return;
        }
        fragment.appendChild(node);
        return;
      }

      if (node.tagName === 'P' && (node.classList.contains('dija-product-note') || sizeAdvice.test(text))) {
        fragment.appendChild(node);
      }
    });

    return fragment.childNodes.length ? fragment : null;
  }

  function cleanDetails(source) {
    Array.from(source.children).filter(isGenericSectionHeading).forEach((node) => node.remove());
    wrapTables(source);
  }

  function moveIntroToPremiumPosition(story, intro) {
    const productInfo = story.closest('product-info');
    const slot = productInfo?.querySelector('[data-dija-intro-slot]');
    if (slot && !intro.hidden) slot.appendChild(intro);
  }

  function initStory(story, storyIndex) {
    if (story.dataset.dijaReady === 'true') return;
    const source = story.querySelector('[data-dija-description-source]');
    const intro = story.querySelector('[data-dija-product-intro]');
    const dynamic = story.querySelector('[data-dija-dynamic-accordions]');
    const detailAccordion = story.querySelector('[data-dija-details-accordion]');
    if (!source || !intro || !dynamic || !detailAccordion) return;

    unwrapDescription(source);
    extractIntro(source, intro);
    const sizeContent = extractSizeSections(source);
    cleanDetails(source);
    moveIntroToPremiumPosition(story, intro);

    if (sizeContent) {
      dynamic.appendChild(buildAccordion(story.dataset.sizeLabel, sizeContent, `DijaSizeGuide-${storyIndex}`));
    }

    if (!normalize(source.textContent) && !source.querySelector('img, table, video, iframe')) {
      detailAccordion.remove();
    }

    story.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        story.querySelectorAll('details[open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });

    story.dataset.dijaReady = 'true';
  }

  function initAll() {
    document.querySelectorAll('[data-dija-product-story]').forEach(initStory);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
